import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { Activity, ActivityUser, deleteActivity, prettyActivityName, toggleActivityMeta } from '../../model/activity';
import { camelCase, isEmpty, isFunction } from 'lodash';
import { t } from '../../locales/translate';
import { Modal } from '../Modal/Modal';
import { ControlledModal } from '../Modal/ControlledModal';
import { Button } from '../Button/Button';
import { Tooltip } from '../Tooltip/Tooltip';
import { formatRelativeDate, formatDateTime } from '../../util/util';
import { Avatar } from '../Avatar/Avatar';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/IconName';
import { CommentList } from './CommentList';
import { ReplyForm } from './ReplyForm';
import { useConfirmation } from '../ConfirmationPrompt/ConfirmationPrompt';
import { handleError } from '../../model/notification';
import { CommentForm } from './CommentForm';
import { Spinner } from '../Spinner/Spinner';
import { useSubmit, useRemoteData } from '../../model/data';
import { getUser, userDisplayLine } from '../../model/user';
import { NavLink } from 'react-router-dom';
import { DeleteActivity } from '../../pages/ticketing/Orders/OrderDetails/ActivityDetails/DeleteActivity';
import { RefundActivity } from '../../pages/ticketing/Orders/OrderDetails/ActivityDetails/RefundActivity';
import { PaymentAddedActivity } from '../../pages/ticketing/Orders/OrderDetails/ActivityDetails/PaymentAddedActivity';
import { SeatsBlockedActivityBody } from '../../pages/ticketing/Events/Event/Contingent/SeatsBlockedActivity';
import ErrorBoundary from '../../pages/ErrorBoundary/ErrorBoundary';
import { UnbookInvoiceActivity } from '../../pages/ticketing/Orders/OrderDetails/ActivityDetails/UnbookInvoiceActivity';
import { ensureSuffix } from '../../util/formatters';
import { ExternalLink } from '../ExternalLink/ExternalLink';

export type CommentAction = 'reply' | 'flagAsShippingRelevant' | 'flagAsProductImportant' | 'edit' | 'delete';

interface Props {
  activity: Activity;
  displayReplies?: boolean;
  actions?: CommentAction[];
  readOnly?: boolean;
  onChange?: () => void;
  onReply?: () => void;
  onFlag?: () => void;
}

export const botUser: ActivityUser = {
  id: 'ks-bot',
  name: 'KS Bot',
  email: '',
};

export function Comment({
  activity,
  displayReplies = true,
  actions = ['reply', 'flagAsShippingRelevant', 'flagAsProductImportant', 'edit', 'delete'],
  readOnly,
  onReply,
  onChange,
  onFlag,
}: Props): JSX.Element {
  const commentRef = useRef<HTMLDivElement>(null);
  const [displayReplyForm, setDisplayReplyForm] = useState(false);
  const [details, setDetails] = useState<React.ReactNode | null>(null);
  const [displayEditModal, setDisplayEditModal] = useState(false);

  const user = activity.user || botUser;
  const typeFunction: string | ((activity: Activity) => string) = t.model.activity.type[camelCase(activity.action)];
  const actionName: string =
    (typeFunction && isFunction(typeFunction) ? typeFunction(activity) : typeFunction) ||
    prettyActivityName(activity.action);

  const [handleSetShippingRelevant, setShippingRelevantLoading] = useSubmit(
    () => toggleActivityMeta({ activity, metaFieldName: 'shipmentRelevant', onFlag }),
    (error) => handleError(error)
  );
  const [handleSetProductImportant, setProductImportantLoading] = useSubmit(
    () => toggleActivityMeta({ activity, metaFieldName: 'productImportant', onFlag }),
    (error) => handleError(error)
  );
  const actionsLoading = setShippingRelevantLoading || setProductImportantLoading;

  const { confirm } = useConfirmation({
    title: t.actions.delete(t.model.activity.comment),
    message: t.general.confirmDelete(t.model.activity.comment),
    action: async () => await deleteActivity(activity.id),
  });

  const availableActions = [
    ...(actions.includes('reply')
      ? [
          {
            icon: 'corner-down-left',
            text: t.model.activity.reply,
            callback: () => {
              if (onReply) {
                onReply();
              } else {
                setDisplayReplyForm(true);
              }
            },
          },
        ]
      : []),
    ...(actions.includes('flagAsShippingRelevant') &&
    activity.$permissions?.includes('update') &&
    activity.type === 'comment' &&
    (activity.subjectType === 'order' || activity.meta?.shipmentRelevant)
      ? [
          {
            icon: 'delivery-truck',
            displayLoading: true,
            className: activity.meta && activity.meta.shipmentRelevant ? 'active' : '',
            text: t.model.activity.markAsShipmentRelevant,
            callback: () => handleSetShippingRelevant(),
          },
        ]
      : []),
    ...(actions.includes('flagAsProductImportant') &&
    activity.$permissions?.includes('update') &&
    activity.type === 'comment' &&
    (activity.subjectType === 'product' || activity.subjectType === 'merch_product' || activity.meta?.productImportant)
      ? [
          {
            icon: 'alert-circle',
            displayLoading: true,
            className: activity.meta?.productImportant ? 'active' : '',
            text: t.model.activity.markAsProductImportant,
            callback: () => handleSetProductImportant(),
          },
        ]
      : []),
    ...(actions.includes('edit') &&
    activity.$permissions &&
    activity.$permissions.includes('update') &&
    activity.type === 'comment'
      ? [
          {
            icon: 'edit',
            text: t.actions.edit(''),
            callback: () => setDisplayEditModal(true),
          },
        ]
      : []),
    ...(actions.includes('delete') && activity.$permissions && activity.$permissions.includes('destroy')
      ? [
          {
            icon: 'trash',
            text: t.actions.delete(''),
            callback: confirm,
          },
        ]
      : []),
    ...(!isEmpty(activity.modifications)
      ? [
          {
            icon: 'debug',
            text: t.actions.debug,
            callback: () => {
              setDetails(<span>{JSON.stringify(activity.modifications, undefined, 2)}</span>);
            },
          },
        ]
      : []),
  ];

  function handleClickOutside(event: MouseEvent): void {
    if (commentRef.current && commentRef.current.contains(event.target as Node)) {
      return;
    }
    setDisplayReplyForm(false);
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
  }, []);

  return (
    <div className="comment" ref={commentRef}>
      <div className="comment__avatar">
        <Avatar image={user.avatar && user.avatar.url} name={user.name || user.email} rounded></Avatar>
      </div>
      <div className="comment__container">
        <header className="comment__header">
          <div className="comment__title-container">
            <div className="comment__title">
              <span>{user.name || user.email}</span>
              {activity.meta?.shipmentRelevant && (
                <Tooltip reference={<Icon name="delivery-truck--sm" />}>{t.model.activity.shipmentRelevant}</Tooltip>
              )}
              {activity.meta?.productImportant && (
                <Tooltip reference={<Icon name="alert-circle--sm" />}>{t.model.activity.productImportant}</Tooltip>
              )}
            </div>

            <div className="comment__date">
              {formatRelativeDate(activity.createdAt)} - {formatDateTime(activity.createdAt, true)}
            </div>
          </div>
          {actions && actionsLoading ? (
            <Spinner color="var(--color-tint)" scale="0.7" />
          ) : (
            <div className="comment__actions">
              {!readOnly &&
                availableActions.map((action) => {
                  return (
                    <Tooltip
                      key={action.icon}
                      reference={
                        <div className={classnames('comment__action', action.className)} onClick={action.callback}>
                          <Icon name={`${action.icon}--sm` as IconName} />
                        </div>
                      }
                      darkMode
                    >
                      <small>{action.text}</small>
                    </Tooltip>
                  );
                })}
            </div>
          )}
        </header>
        <div className="comment__body">
          <div>{activity.content}</div>
          <div>
            {activity.action &&
              activity.action !== 'comment' &&
              activity.action !== 'paypal_webhook_processed' &&
              activity.action !== 'process_payment_authorization' && (
                <span className="activity__type">{ensureSuffix(actionName, '.')}</span>
              )}
          </div>
          <ErrorBoundary inline>
            {activity.action === 'contingent_change' && (
              <span>{t.model.activity.contingentChangeMessage(activity.modifications)}</span>
            )}
            {(activity.action === 'seats_blocked' || activity.action === 'seats_unblocked') && (
              <SeatsBlockedActivityBody activity={activity} />
            )}
            {activity.action === 'preferred_payment_method_updated' && (
              <span>
                {t.model.activity.preferredPaymentMethodUpdate({
                  from: t.model.bank[camelCase(activity.modifications.from)],
                  to: t.model.bank[camelCase(activity.modifications.to)],
                })}
              </span>
            )}
            {(activity.action === 'user_changed' || activity.action === 'user_removed') && activity.recordId && (
              <CommentRecord record={activity.recordId} />
            )}
            {activity.action === 'items_refunded' && <RefundActivity activity={activity} />}
            {(activity.action === 'outgoing_items_removed' ||
              activity.action === 'auto_cancelled' ||
              activity.action === 'auto_archived') && <DeleteActivity activity={activity} />}
            {activity.action === 'payment_added_by_admin' && <PaymentAddedActivity activity={activity} />}
            {activity.action === 'unbook_invoice' && <UnbookInvoiceActivity activity={activity} />}
            {activity.action === 'paypal_webhook_processed' && (
              <div>
                PayPal: {activity.modifications.summary} ({activity.modifications.resource?.id}{' '}
                <ExternalLink
                  url={`https://ks-tools.krasserstoff.com/ks-tools/paypal?transactionId=${activity.modifications.resource?.id}`}
                />
                <ExternalLink
                  url={`https://www.paypal.com/unifiedtransactions/details/payment/${activity.modifications.resource?.id}`}
                  icon="money"
                />
                )
              </div>
            )}
            {activity.action === 'process_payment_authorization' &&
              t.model.activity.processPaymentAuthorization[activity.modifications.action]}
          </ErrorBoundary>
        </div>
      </div>
      {displayReplies && activity.comments && (
        <div className="comment__replies">
          <CommentList
            readOnly={readOnly}
            actions={['reply', 'edit', 'delete']}
            comments={activity.comments}
            onChange={onChange}
            onReply={() => {
              setDisplayReplyForm(true);
            }}
          />
        </div>
      )}
      {displayReplyForm && (
        <div className="comment__reply-form">
          <ReplyForm
            subjectId={activity.id}
            subjectType={activity.type}
            placeholder={t.model.activity.writeComment}
            autofocus={true}
            onSubmit={() => {
              setDisplayReplyForm(false);
              if (onChange) {
                onChange();
              }
            }}
            onCancel={() => {
              setDisplayReplyForm(false);
            }}
          />
        </div>
      )}
      <ControlledModal<Partial<Activity>>
        open={displayEditModal}
        model={{
          id: activity.id,
          content: activity.content,
        }}
        onClose={() => setDisplayEditModal(false)}
        title={t.actions.edit(t.model.activity.comment)}
        size="small"
      >
        {(controller) => <CommentForm onChange={onChange} controller={controller} />}
      </ControlledModal>
      <Modal
        open={!!details}
        className="comment__details"
        title={t.general.details}
        footer={<Button type="primary" title={t.actions.close('')} onClick={() => setDetails(null)} />}
      >
        {details}
      </Modal>
    </div>
  );
}

function CommentRecord({ record }: { record: string }): JSX.Element | null {
  const [user, loading, error] = useRemoteData(
    async () => await getUser(record),
    (db) => {
      return db.table('users').where({ id: record })[0];
    }
  );

  if (loading) {
    return <small className="comment__record">{t.actions.loading(t.model.user.customer)}...</small>;
  }

  if (!user || error) {
    return <small className="comment__record">{t.errors.itemNotFound(t.model.user.customer)}</small>;
  }

  return (
    <NavLink className="button button--link button--small" to={`/user/${user.id}`}>
      {userDisplayLine(user)}
    </NavLink>
  );
}
